/**
 * Back to Top Button Component
 * Shows a floating button when user scrolls past 2x viewport height
 * Smoothly scrolls to top when clicked
 * Used in Shop by Brand section
 */

if (!customElements.get('back-to-top-button')) {
  class BackToTopButton extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('.back-to-top-brand');
      this.scrollThreshold = window.innerHeight * 2;
      this.updateTimeout = null;
      this.resizeTimeout = null;
      this.isVisible = false;
    }

    connectedCallback() {
      // Wait for next frame to ensure element is fully rendered
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      if (!this.button) {
        console.warn('Back to top button: button element not found');
        return;
      }

      // Bind methods
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);

      // Initial state update
      this.updateButtonVisibility();

      // Listen to scroll events with passive flag for better performance
      window.addEventListener('scroll', this.handleScroll, { passive: true });

      // Listen to resize events to recalculate threshold
      window.addEventListener('resize', this.handleResize);

      // Listen for breakpoint changes (Enterprise theme pattern)
      window.addEventListener('on:breakpoint-change', this.handleResize);

      // Click event to scroll to top
      this.button.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      // Clean up event listeners
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('on:breakpoint-change', this.handleResize);

      if (this.button) {
        this.button.removeEventListener('click', this.handleClick);
      }

      if (this.updateTimeout) {
        cancelAnimationFrame(this.updateTimeout);
      }

      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
    }

    handleScroll() {
      // Use requestAnimationFrame for smooth, performant updates
      if (!this.updateTimeout) {
        this.updateTimeout = requestAnimationFrame(() => {
          this.updateButtonVisibility();
          this.updateTimeout = null;
        });
      }
    }

    handleResize() {
      // Debounce resize events
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        // Recalculate threshold based on new viewport height
        this.scrollThreshold = window.innerHeight * 2;
        this.updateButtonVisibility();
      }, 150);
    }

    handleClick(event) {
      event.preventDefault();

      // Check if user prefers reduced motion
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Scroll to top with smooth behavior (if motion is allowed)
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });

      // Optional: Dispatch custom event for analytics or other tracking
      this.dispatchEvent(new CustomEvent('back-to-top:clicked', {
        bubbles: true,
        detail: { scrollPosition: window.scrollY }
      }));
    }

    updateButtonVisibility() {
      if (!this.button) return;

      const currentScroll = window.scrollY || window.pageYOffset;
      const shouldBeVisible = currentScroll > this.scrollThreshold;

      // Only update DOM if state actually changed (performance optimization)
      if (shouldBeVisible !== this.isVisible) {
        this.isVisible = shouldBeVisible;
        this.button.classList.toggle('is-visible', shouldBeVisible);

        // Update aria-hidden for accessibility
        this.button.setAttribute('aria-hidden', !shouldBeVisible);
      }
    }
  }

  customElements.define('back-to-top-button', BackToTopButton);
}
